import { useState, type ReactNode } from 'react';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { DateInput } from '@/components/ui/date-input';
import { LedgerViewSelect } from '@/components/general-ledger/LedgerViewSelect';
import { useFundMetrics } from '@/hooks/react-query/useFunds';
import { DEFAULT_LEDGER_VIEW, type LedgerView } from '@/types/drawdown';
import type { FundMetrics } from '@/types/fund';
import { getTodayAsISODate } from '@/utils/date-operations';
import {
  formatCurrency,
  formatNumber,
  formatPercentage,
} from '@/utils/formatters';

interface FundMetricsCardProps {
  fundId: string;
  currency?: string;
}

interface MetricProps {
  label: string;
  children: ReactNode;
}

function Metric({ label, children }: MetricProps) {
  return (
    <div>
      <p className="text-sm font-medium text-muted-foreground">{label}</p>
      <p className="text-sm text-foreground">{children}</p>
    </div>
  );
}

export function FundMetricsCard({
  fundId,
  currency = 'EUR',
}: FundMetricsCardProps) {
  const today = getTodayAsISODate();
  const [view, setView] = useState<LedgerView>(DEFAULT_LEDGER_VIEW);
  const [asOf, setAsOf] = useState(today);
  // The browser's today can be a day ahead of the server's, which rejects
  // future dates, so today is left to the server's default.
  const requestedAsOf = asOf === today ? undefined : asOf;
  const { data: metrics, error } = useFundMetrics(fundId, view, requestedAsOf);

  if (!metrics && !error) {
    return null;
  }

  return (
    <Card>
      <CardHeader className="flex flex-row items-start justify-between gap-4 space-y-0">
        <div className="space-y-1.5">
          <CardTitle>Fund Metrics</CardTitle>
          <CardDescription>
            Key performance indicators and capital summary
          </CardDescription>
        </div>
        <div className="flex items-center gap-2">
          <label
            htmlFor="fund-metrics-as-of"
            className="text-sm text-muted-foreground whitespace-nowrap"
          >
            End Date
          </label>
          <div className="w-40">
            <DateInput
              id="fund-metrics-as-of"
              className="h-8"
              value={asOf}
              max={today}
              onChange={(value) => setAsOf(value || today)}
            />
          </div>
          <LedgerViewSelect value={view} onChange={setView} />
        </div>
      </CardHeader>
      {metrics ? (
        <FundMetricsContent
          metrics={metrics}
          currency={metrics.currency || currency}
        />
      ) : (
        <CardContent>
          <p className="text-sm text-muted-foreground">
            Metrics could not be loaded for this date.
          </p>
        </CardContent>
      )}
    </Card>
  );
}

interface FundMetricsContentProps {
  metrics: FundMetrics;
  currency: string;
}

function FundMetricsContent({ metrics, currency }: FundMetricsContentProps) {
  const money = (value: Parameters<typeof formatCurrency>[0]) =>
    formatCurrency(value, { currency });

  return (
    <CardContent className="space-y-6">
      <div>
        <h4 className="text-sm font-semibold text-foreground mb-3">
          Performance Multiples
        </h4>
        <div className="grid grid-cols-2 gap-4">
          <Metric label="TVPI">
            {metrics.tvpi ? `${formatNumber(metrics.tvpi)}x` : '-'}
          </Metric>
          <Metric label="DPI">
            {metrics.dpi ? `${formatNumber(metrics.dpi)}x` : '-'}
          </Metric>
          <Metric label="RVPI">
            {metrics.rvpi ? `${formatNumber(metrics.rvpi)}x` : '-'}
          </Metric>
          <Metric label="Gross MOIC">
            {metrics.gross_moic ? `${formatNumber(metrics.gross_moic)}x` : '-'}
          </Metric>
        </div>
      </div>

      <div>
        <h4 className="text-sm font-semibold text-foreground mb-3">
          Internal Rate of Return
        </h4>
        <div className="grid grid-cols-2 gap-4">
          <Metric label="IRR">
            <span className="text-xs text-muted-foreground">Net: </span>
            {metrics.net_irr != null ? formatPercentage(metrics.net_irr) : '-'}
          </Metric>
        </div>
      </div>

      <div>
        <h4 className="text-sm font-semibold text-foreground mb-3">
          Capital Summary
        </h4>
        <div className="grid grid-cols-2 gap-4">
          <Metric label="Total Commitments">
            {money(metrics.total_commitments)}
          </Metric>
          <Metric label="Total Contributed">
            {money(metrics.total_contributed)}
          </Metric>
          <Metric label="Total Distributed">
            {money(metrics.total_distributed)}
          </Metric>
          <Metric label="NAV">{money(metrics.nav)}</Metric>
          <Metric label="Gross Asset Value">
            {money(metrics.gross_asset_value)}
          </Metric>
          <Metric label="Invested Cost">{money(metrics.invested_cost)}</Metric>
          <Metric label="Invested Capital">
            {money(metrics.invested_capital)}
          </Metric>
          <Metric label="Notes Outstanding">
            {money(metrics.notes_outstanding)}
          </Metric>
          <Metric label="Cash">{money(metrics.cash_balance)}</Metric>
          <Metric label="Called Capital">
            {formatPercentage(metrics.called_capital_percentage)}
          </Metric>
          <Metric label="Dry Powder">{money(metrics.uncalled_capital)}</Metric>
          <Metric label="Working Capital">
            {money(metrics.working_capital)}
          </Metric>
          <Metric label="Unrealized Gain/Loss">
            {money(metrics.unrealized_gain_loss)}
          </Metric>
          <Metric label="Realized Gain/Loss">
            {money(metrics.realized_gain_loss)}
          </Metric>
          <div>
            <p className="text-sm font-medium text-muted-foreground">
              Accrued Management Fees
            </p>
            <p className="text-sm text-foreground">
              {money(metrics.accrued_management_fees)}
            </p>
            {metrics.has_active_fee_schedule && metrics.management_fee_rate && (
              <p className="text-xs text-muted-foreground">
                {formatPercentage(metrics.management_fee_rate)}
                {metrics.management_fee_basis &&
                  ` of ${metrics.management_fee_basis}`}
              </p>
            )}
          </div>
          <Metric label="Paid Management Fees">
            {money(metrics.paid_management_fees)}
          </Metric>
          <Metric label="Unpaid Management Fees">
            {money(metrics.unpaid_management_fees)}
          </Metric>
          <Metric label="Prepaid Management Fees">
            {money(metrics.prepaid_management_fees)}
          </Metric>
          <Metric label="Fund Expenses">{money(metrics.fund_expenses)}</Metric>
          <Metric label="Deal Costs Expensed">
            {money(metrics.deal_costs_expensed)}
          </Metric>
          {!metrics.has_active_fee_schedule && (
            <p className="text-xs text-muted-foreground italic col-span-2">
              No active management fee schedule configured
            </p>
          )}
        </div>
      </div>

      <div>
        <h4 className="text-sm font-semibold text-foreground mb-3">
          Portfolio & Risk
        </h4>
        <div className="grid grid-cols-2 gap-4">
          <Metric label="Portfolio Companies">
            {metrics.portfolio_company_count}
          </Metric>
          <Metric label="Active Companies">
            {metrics.active_company_count}
          </Metric>
          <Metric label="Exited Companies">
            {metrics.exited_company_count}
          </Metric>
          <Metric label="Concentration Limit">
            {formatPercentage(metrics.concentration_limit)}
          </Metric>
          <Metric label="Loss Ratio">
            {formatPercentage(metrics.loss_ratio)}
          </Metric>
          <Metric label="Graduation Rate">
            {formatPercentage(metrics.graduation_rate)}
          </Metric>
          <Metric label="Initial Capital">
            {money(metrics.initial_capital)}
          </Metric>
          <Metric label="Follow-on Capital">
            {money(metrics.follow_on_capital)}
          </Metric>
          <Metric label="Follow-on Ratio">
            {formatPercentage(metrics.follow_on_ratio)}
          </Metric>
          <Metric label="GP Commitment">
            {formatPercentage(metrics.gp_commitment_percentage)}
            {metrics.gp_commitment_amount && (
              <span className="text-xs text-muted-foreground ml-1">
                ({money(metrics.gp_commitment_amount)})
              </span>
            )}
          </Metric>
        </div>
      </div>
    </CardContent>
  );
}
